'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { MaskedInput } from '@curology/ui-components-web/react/MaskedInput';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { DATA_KEYS } from '@/consts/dataKeys';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { UserService } from '@/services/userService/client';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { type InputState } from './types/types';
import {
  getBirthdate,
  getPrescriptionBottleName,
  isBirthdateValid,
  isLegalFirstNameValid,
  isLegalLastNameValid,
  isUnder13,
} from './utils/utils';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';

export const LegalName = () => {
  const patient = useDataProvider(DATA_KEYS.patient);
  const { visit } = useVisitContext();
  const { onNext, onBack } = useNavigation();
  const { brand } = useFlowContext();
  const flashMessages = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);

  const [legalFirstName, setLegalFirstName] = useState(
    patient?.firstname || '',
  );

  const [legalFirstNameInputState, setLegalFirstNameInputState] =
    useState<InputState>();

  const [legalLastName, setLegalLastName] = useState(patient?.lastname || '');

  const [legalLastNameInputState, setLegalLastNameInputState] =
    useState<InputState>();

  const [dob, setDob] = useState(getBirthdate(patient));

  const [dobInputState, setDobInputState] = useState<InputState>();

  const [dobInputHelperTextState, setDobInputHelperTextState] = useState<
    string | undefined
  >();

  const [inputsDisabled, setInputsDisabled] = useState(false);

  const showDobInput =
    visit?.sessionBag?.getDobOnLegalNamePage ||
    (patient && !patient.date_of_birth);

  const setSubmitting = () => {
    setLegalFirstNameInputState(undefined);
    setLegalLastNameInputState(undefined);
    setInputsDisabled(true);
  };

  const submitForm = async () => {
    setSubmitting();

    if (!isLegalLastNameValid(legalLastName)) {
      setLegalLastNameInputState('critical');
      return setInputsDisabled(false);
    }

    if (!isLegalFirstNameValid(legalFirstName)) {
      setLegalFirstNameInputState('critical');
      return setInputsDisabled(false);
    }

    if (showDobInput) {
      if (!isBirthdateValid(dob)) {
        setDobInputState('critical');
        setInputsDisabled(false);
        setDobInputHelperTextState('Please enter a valid date of birth.');
        return;
      } else if (isUnder13(dob)) {
        setDobInputState('critical');
        setInputsDisabled(false);
        setDobInputHelperTextState(
          `The birthday entered shows you're under 13, which means we can't provide prescriptions just yet.`,
        );
        return;
      }
    }

    try {
      await UserService.updateLegalName(
        brand,
        legalFirstName,
        legalLastName,
        dob,
      );

      trackEvent(AnalyticsEvent.LEGAL_NAME_PAGE_COMPLETED, {
        userId: patient?.id,
      });

      onNext();
    } catch (exception: any) {
      if (handleIfSessionExpired(exception.response?.status)) return;
      flashMessages.error({
        title: 'Sorry!',
        message: 'A system error occurred. Please try again.',
      });
      setInputsDisabled(false);
    }
  };

  const bottleLabelBackgroundImageUrl =
    brand === 'agency'
      ? `url('https://images.ctfassets.net/8pzh4g8cs30t/1aW411M0NiLYGesUlmPgrI/d4d32d1c8956ca294eea0bb2125423a7/agency_bottle_label_gradient_bg_image.png')`
      : `url('https://images.ctfassets.net/mdcr7mahi0vp/4ebCZfxTEiG1iDpX6I9NkI/52064452e2e5cc3bdcdf0818bc1ee990/bottle_label_gradient_bg_image.png')`;

  const legalNameTextColor =
    brand === 'agency'
      ? 'cur-text-agency-secondary-500'
      : 'cur-text-curology-brand-500';

  return (
    <>
      <Typography className="cur-text-primary" size="h2">
        What’s your name?
      </Typography>
      <Typography className="cur-leading-6 cur-whitespace-pre-line">
        Tell us whose name to put on the bottle—full legal name, please! (Your
        formula is a medical prescription.)
      </Typography>
      <div
        className="cur-bg-no-repeat cur-bg-[length:100%_100%] cur-pl-10 cur-pr-4 cur-py-12 data-hj-suppress"
        style={{
          backgroundImage: bottleLabelBackgroundImageUrl,
        }}
      >
        <Typography
          className={`cur-overflow-hidden cur-text-ellipsis cur-whitespace-nowrap ${legalNameTextColor}`}
          size="h2"
        >
          {getPrescriptionBottleName(legalFirstName, legalLastName)}
        </Typography>
      </div>
      <div className="cur-flex cur-flex-col cur-gap-2 data-hj-suppress">
        <TextInput
          disabled={inputsDisabled}
          helperText={
            legalFirstNameInputState === 'critical'
              ? 'Please enter a valid legal first name.'
              : undefined
          }
          label="Legal first name"
          onChange={(event) => {
            setLegalFirstName(event.target.value || '');
            setLegalFirstNameInputState(undefined);
          }}
          onFocus={() => setLegalFirstNameInputState(undefined)}
          state={legalFirstNameInputState}
          value={legalFirstName}
        />
        <TextInput
          disabled={inputsDisabled}
          helperText={
            legalLastNameInputState === 'critical'
              ? 'Please enter a valid legal last name.'
              : undefined
          }
          label="Legal last name"
          onChange={(event) => {
            setLegalLastName(event.target.value || '');
            setLegalLastNameInputState(undefined);
          }}
          onFocus={() => setLegalLastNameInputState(undefined)}
          state={legalLastNameInputState}
          value={legalLastName}
        />
        {showDobInput && (
          <MaskedInput
            autoComplete="bday"
            disabled={inputsDisabled}
            enterKeyHint="next"
            helperText={dobInputHelperTextState}
            label="Date of birth"
            mask={[/[0-9]/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
            onChange={(event) => {
              setDob(event.target.value || '');
              setDobInputState(undefined);
              setDobInputHelperTextState(undefined);
            }}
            onFocus={() => {
              setDobInputState(undefined);
              setDobInputHelperTextState(undefined);
            }}
            placeholder="mm/dd/yyyy"
            state={dobInputState}
            type="text"
            value={dob}
          />
        )}
      </div>
      <div className="cur-flex cur-flex-col cur-gap-2">
        <Button
          className="cur-w-full"
          disabled={inputsDisabled}
          onClick={submitForm}
        >
          Next
        </Button>
        <Button
          buttonType="text"
          className="cur-text-primary cur-w-full"
          disabled={inputsDisabled}
          onClick={() => {
            setInputsDisabled(true);

            onBack();
          }}
        >
          Back
        </Button>
      </div>
    </>
  );
};
